import { useState, useEffect } from "react";
import { RefreshCw, Mail, Send, File, Loader2 } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import {
  syncContactEmails,
  fetchContactEmails,
  fetchEmailDetail,
  summarizeContactEmails,
} from "../../services/api";
import { getGraphToken } from "../../services/msal";

export default function EmailHistory({ contactId, contactEmail }) {
  const [emails, setEmails] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedBody, setExpandedBody] = useState(null);

  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    fetchContactEmails(contactId, { limit: 50 })
      .then((data) => {
        setEmails(data.emails || []);
        if (data.summary) setSummary(data.summary);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [contactId]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const graphToken = await getGraphToken();
      const result = await syncContactEmails(contactId, graphToken);
      setSyncResult(result);
      const data = await fetchContactEmails(contactId, { limit: 50 });
      setEmails(data.emails || []);
      if (data.summary) setSummary(data.summary);
    } catch (err) {
      setSyncResult({ error: err.message });
    } finally {
      setSyncing(false);
    }
  };

  const handleSummarize = async () => {
    setSummarizing(true);
    try {
      const result = await summarizeContactEmails(contactId);
      setSummary({ text: result.summary, generatedAt: result.generatedAt });
    } catch (err) {
      setSummary({ text: `Error: ${err.message}`, generatedAt: null });
    } finally {
      setSummarizing(false);
    }
  };

  const handleExpand = async (email) => {
    if (expandedId === email.id) {
      setExpandedId(null);
      setExpandedBody(null);
      return;
    }
    setExpandedId(email.id);
    setExpandedBody(null);
    if (!email.isParty) return;
    try {
      const detail = await fetchEmailDetail(contactId, email.id);
      setExpandedBody(detail.body || detail.bodyPreview || "");
    } catch {
      setExpandedBody("Unable to load email body.");
    }
  };

  if (!contactEmail) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h4 style={{ margin: 0, fontFamily: fonts.heading, fontSize: 14, color: colors.text }}>
          Email History
        </h4>
        <div style={{ display: "flex", gap: 6 }}>
          {emails.length > 0 && (
            <button
              onClick={handleSummarize}
              disabled={summarizing}
              style={{
                padding: "4px 10px", fontSize: 11, fontFamily: fonts.body, fontWeight: 600,
                border: `1px solid ${colors.border}`, borderRadius: 6,
                background: colors.bgCard, color: colors.accent, cursor: summarizing ? "wait" : "pointer",
              }}
            >
              {summarizing ? "Summarizing..." : "Summarize with AI"}
            </button>
          )}
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{
              padding: "4px 10px", fontSize: 11, fontFamily: fonts.body, fontWeight: 600,
              border: `1px solid ${colors.border}`, borderRadius: 6,
              background: colors.accent, color: "#fff", cursor: syncing ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: 4,
            }}
          >
            <RefreshCw size={12} className={syncing ? "spin" : undefined} />
            {syncing ? "Syncing..." : "Sync Emails"}
          </button>
        </div>
      </div>

      {syncResult && (
        <div style={{
          padding: "6px 10px", marginBottom: 8, borderRadius: 6, fontSize: 11, fontFamily: fonts.body,
          background: syncResult.error ? colors.danger + "15" : colors.success + "15",
          color: syncResult.error ? colors.danger : colors.success,
        }}>
          {syncResult.error
            ? syncResult.error
            : `Synced ${syncResult.synced} new, ${syncResult.updated} updated (${syncResult.total} total)`}
        </div>
      )}

      {summary && (
        <div style={{
          padding: "10px 12px", marginBottom: 12, borderRadius: 8,
          background: colors.bgHover, border: `1px solid ${colors.border}`,
          fontSize: 12, fontFamily: fonts.body, color: colors.text, lineHeight: 1.5,
          whiteSpace: "pre-wrap",
        }}>
          <div style={{ fontSize: 10, color: colors.textSecondary, marginBottom: 6, fontWeight: 600 }}>
            AI Summary {summary.generatedAt && `· ${new Date(summary.generatedAt).toLocaleDateString()}`}
          </div>
          {summary.text}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 16 }}>
          <Loader2 size={16} className="spin" color={colors.textSecondary} />
        </div>
      ) : emails.length === 0 ? (
        <div style={{
          textAlign: "center", padding: 20, fontSize: 12, color: colors.textSecondary, fontFamily: fonts.body,
        }}>
          No emails synced yet. Click "Sync Emails" to import from your O365 mailbox.
        </div>
      ) : (
        <div style={{
          border: `1px solid ${colors.border}`, borderRadius: 8, overflow: "hidden",
          maxHeight: 400, overflowY: "auto",
        }}>
          {emails.map((email, i) => {
            const isSent = email.direction === "sent";
            const IconComp = isSent ? Send : Mail;
            const iconColor = isSent ? "#6366f1" : "#0ea5e9";
            const isExpanded = expandedId === email.id;

            return (
              <div key={email.id || i}>
                <div
                  onClick={() => handleExpand(email)}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 12px",
                    borderBottom: i < emails.length - 1 ? `1px solid ${colors.border}` : "none",
                    cursor: email.isParty ? "pointer" : "default",
                    background: isExpanded ? colors.bgHover : "transparent",
                  }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: 4,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: iconColor + "18", flexShrink: 0, marginTop: 1,
                  }}>
                    <IconComp size={12} color={iconColor} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 12, fontFamily: fonts.body, color: colors.text,
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {email.subject || "(no subject)"}
                    </div>
                    {email.isParty && email.from && (
                      <div style={{ fontSize: 10, color: colors.textSecondary, fontFamily: fonts.body, marginTop: 2 }}>
                        {isSent ? `To: ${email.toRecipients?.map((r) => r.name || r.email).join(", ") || ""}` : `From: ${email.from.name || email.from.email}`}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: colors.textSecondary, fontFamily: fonts.mono, whiteSpace: "nowrap", flexShrink: 0 }}>
                    {email.sentAt ? new Date(email.sentAt).toLocaleDateString() : ""}
                  </div>
                  {email.hasAttachments && <File size={10} color={colors.textSecondary} style={{ flexShrink: 0, marginTop: 3 }} />}
                </div>
                {isExpanded && email.isParty && (
                  <div style={{
                    padding: "8px 12px 12px 42px", fontSize: 12, fontFamily: fonts.body,
                    color: colors.text, borderBottom: i < emails.length - 1 ? `1px solid ${colors.border}` : "none",
                    background: colors.bgHover, lineHeight: 1.5,
                  }}>
                    {expandedBody === null ? (
                      <Loader2 size={14} className="spin" color={colors.textSecondary} />
                    ) : (
                      <div dangerouslySetInnerHTML={{ __html: expandedBody }} style={{ maxHeight: 300, overflow: "auto" }} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
